import { useState, useEffect } from "react";
import { baImageUrl } from "../../utils/baImageUrl";

// Image-migration read-swap (server Phase C). Renders a photo with a fallback
// CASCADE so it's fast in the common case and self-heals on the rare miss:
//
//   1. `url`  — the DIRECT S3/CloudFront url (s3ThumbUrl for grids, s3ImageUrl
//               for full view). Public = permanent CloudFront (never errors).
//               Private = a signed url (works within its expiry window).
//   2. `/ba-image/<photoId>` — the server RE-SIGNS a fresh url on every request,
//               so this is the safety net when a private signed url has expired
//               on a stale re-render. (A plain <img> can't mint its own signed
//               url — the box does it here, on error only.) Survives Phase G:
//               base64 removal nulls imageData, NOT s3Key, so the route still
//               finds the key and re-signs.
//   3. `base64` — legacy imageData, an extra soak-era layer; gone after Phase G.
//
// The box stays OUT of the hot path: public photos load direct and never reach
// step 2; private only fall through on the rare expiry. Pass `variant="thumb"`
// so the /ba-image fallback also serves the thumbnail in grids.
//
// Any extra <img> props (className, style, onClick, loading, ...) pass through.
export default function SmartImage({ url, photoId, base64, variant, alt = "", ...imgProps }) {
  const [stage, setStage] = useState(0);

  // reset to the top of the cascade when the photo changes (list re-render).
  // key off url/photoId — base64 is large and identifies the same photo anyway.
  useEffect(() => setStage(0), [url, photoId]);

  const base64Src = base64
    ? base64.startsWith("data:")
      ? base64
      : `data:image/jpeg;base64,${base64}`
    : undefined;

  // ordered list of available sources (skips any that don't exist)
  const sources = [];
  if (url) sources.push(url);
  if (photoId) sources.push(baImageUrl(photoId, variant ? { variant } : undefined));
  if (base64Src) sources.push(base64Src);

  const src = sources[stage];
  if (!src) return null;

  return (
    <img
      src={src}
      alt={alt}
      // advance to the next source in the cascade; past the end → null (placeholder)
      onError={() => setStage((prev) => prev + 1)}
      {...imgProps}
    />
  );
}
